import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import {
  FaBuilding, FaIndustry, FaGlobe, FaMapMarkerAlt, FaUsers,
  FaEnvelope, FaPhone, FaLinkedin, FaTwitter, FaFacebook,
  FaArrowLeft, FaBriefcase, FaHandshake, FaChartLine, FaLightbulb,
  FaUserTie, FaGraduationCap, FaCalendarAlt, FaClock, FaSuitcase
} from 'react-icons/fa';
import predefinedUsers from '../components/auth/usersdata.json';

const InstituteProfilePage = () => {
  const { id } = useParams();
  const institute = predefinedUsers.users.find(org => org.id === id) || 
                       JSON.parse(localStorage.getItem('instituteDirectory') || '[]')
                           .find(org => org.id === id);

  const associatedAlumni = predefinedUsers.users.filter(user => user.instituteId === id) || 
                           JSON.parse(localStorage.getItem('alumniDirectory') || '[]')
                               .filter(user => user.instituteId === id);

  if (!institute) {
    return <div>Institute not found</div>;
  }

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3 } }
  };

  return (
    <motion.div 
      className="min-h-screen bg-gradient-to-br from-blue-100 to-indigo-200 py-12 px-4 sm:px-6 lg:px-8"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="max-w-4xl mx-auto">
        <Link to="/institute-directory" className="inline-flex items-center text-indigo-600 hover:text-indigo-800 mb-6">
          <FaArrowLeft className="mr-2" />
          Back to Directory
        </Link>
        <motion.div className="bg-white shadow-xl rounded-lg overflow-hidden" variants={itemVariants}>
          <div className="p-6 sm:p-10">
            <div className="flex flex-col sm:flex-row items-center mb-6">
              <motion.div 
                className="w-32 h-32 bg-gray-200 rounded-full flex items-center justify-center mb-4 sm:mb-0 sm:mr-6"
                whileHover={{ scale: 1.1 }}
              >
                <FaBuilding className="text-5xl text-gray-500" />
              </motion.div>
              <div className="text-center sm:text-left">
                <h1 className="text-3xl font-bold text-gray-900">{institute.instituteName}</h1>
                <p className="text-xl text-gray-600">{institute.industry}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div variants={itemVariants} className="flex items-center">
                <FaIndustry className="text-2xl text-blue-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Industry</p>
                  <p className="font-semibold">{institute.industry}</p>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="flex items-center">
                <FaMapMarkerAlt className="text-2xl text-red-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <p className="font-semibold">{institute.location || 'Not specified'}</p>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="flex items-center">
                <FaGlobe className="text-2xl text-green-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Website</p>
                  <a href={institute.website} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:text-blue-800">
                    {institute.website || 'Not specified'}
                  </a>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="flex items-center">
                <FaEnvelope className="text-2xl text-purple-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-semibold">{institute.email}</p>
                </div>
              </motion.div>

              {institute.phone && (
                <motion.div variants={itemVariants} className="flex items-center">
                  <FaPhone className="text-2xl text-teal-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-semibold">{institute.phone}</p>
                  </div>
                </motion.div>
              )}

              {institute.linkedIn && (
                <motion.div variants={itemVariants} className="flex items-center">
                  <FaLinkedin className="text-2xl text-blue-700 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">LinkedIn</p>
                    <a href={institute.linkedIn} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:text-blue-800">
                      Company Page
                    </a>
                  </div>
                </motion.div>
              )}
            </div>

            <motion.div variants={itemVariants} className="mt-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <FaLightbulb className="mr-2 text-yellow-500" />
                About the Institute
              </h2>
              <p className="text-gray-700">{institute.description}</p>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <FaUsers className="mr-2 text-indigo-500" />
                Associated Alumni
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {associatedAlumni.map(alumni => (
                  <Link key={alumni.id} to={`/alumni/${alumni.id}`} className="block p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                    <h3 className="font-semibold text-lg">{alumni.fullName}</h3>
                    <p className="text-gray-600">{alumni.major}</p>
                    <p className="text-sm text-gray-500">Class of {alumni.graduationYear}</p>
                  </Link>
                ))}
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8 flex flex-wrap justify-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-blue-500 text-white px-6 py-3 rounded-full font-semibold flex items-center"
              >
                <FaHandshake className="mr-2" />
                Partner with Us
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-green-500 text-white px-6 py-3 rounded-full font-semibold flex items-center"
              >
                <FaBriefcase className="mr-2" />
                View Job Openings
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default InstituteProfilePage;